import { ApiError, apiClient, apiFetch, bearer, unwrap } from './client';
import type {
  AuditRunListOut,
  AuditRunOut,
  AuditRunSourceType,
  FilingCreate,
  FilingOut,
  FilingPreviewOut,
  MaterialityPreviewOut,
  OrphanItcEntriesOut,
} from './types';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '') as string;

export function listRunsForEngagement(
  engagementId: string,
  idToken: string,
): Promise<AuditRunListOut> {
  return apiClient
    .GET('/api/engagements/{engagement_id}/runs', {
      params: { path: { engagement_id: engagementId } },
      headers: bearer(idToken),
    })
    .then(unwrap<AuditRunListOut>);
}

export function getRun(id: string, idToken: string): Promise<AuditRunOut> {
  return apiClient
    .GET('/api/runs/{run_id}', {
      params: { path: { run_id: id } },
      headers: bearer(idToken),
    })
    .then(unwrap<AuditRunOut>);
}

export function deleteAuditRun(id: string, idToken: string): Promise<void> {
  // DELETE returns 204 No Content; unwrap returns null.
  return apiClient
    .DELETE('/api/runs/{run_id}', {
      params: { path: { run_id: id } },
      headers: bearer(idToken),
    })
    .then(unwrap<void>);
}

export interface TriggerAuditRunInput {
  /** Multipart upload for the transactions file. Exclusive with
   *  `transactions_file_id` — the backend rejects providing both or
   *  neither with a 400. */
  transactions?: File | null;
  /** Pool file id (from `GET /api/engagements/{id}/files`) to reuse
   *  instead of re-uploading bytes. */
  transactions_file_id?: string | null;
  /** Optional — required only for sources in SOURCES_REQUIRING_SUPPLIER_FILE
   *  (today: QBO). Netsuite needs neither shape. Same XOR rule applies. */
  suppliers?: File | null;
  suppliers_file_id?: string | null;
  rule_set_id?: string;
  source_type?: AuditRunSourceType;
  as_of?: string;
  /** When true, the engine copies disposition state (approved /
   *  rejected / needs_review) from a prior run onto matching new
   *  findings by `transaction_id`. INP-385. */
  carry_forward?: boolean;
  /** Source-run id to copy dispositions from. When omitted (and
   *  `carry_forward` is true), the server resolves to the engagement's
   *  published run, then the most recent completed run; returns 422
   *  when neither is available. */
  carry_forward_from?: string | null;
  /** De-minimis floor in CAD (INP-366) — recovery findings strictly
   *  under it are auto-rejected with reason code `immaterial_amount`
   *  after carry-forward applies. Server default is 1.00; 0 disables
   *  the sweep. */
  materiality_floor?: number;
}

/** Multipart upload — kept on the legacy `apiFetch` path because
 *  openapi-fetch's request shape doesn't compose ergonomically with
 *  `FormData` (the typed `body` parameter wants the JSON schema
 *  body, not multipart parts). Migration of multipart routes is a
 *  follow-up; the legacy helper still validates against the server. */
export function triggerAuditRun(
  engagementId: string,
  input: TriggerAuditRunInput,
  idToken: string,
): Promise<AuditRunOut> {
  const form = new FormData();
  if (input.transactions) form.append('transactions', input.transactions);
  if (input.transactions_file_id) {
    form.append('transactions_file_id', input.transactions_file_id);
  }
  if (input.suppliers) form.append('suppliers', input.suppliers);
  if (input.suppliers_file_id) form.append('suppliers_file_id', input.suppliers_file_id);
  if (input.rule_set_id) form.append('rule_set_id', input.rule_set_id);
  if (input.source_type) form.append('source_type', input.source_type);
  if (input.as_of) form.append('as_of', input.as_of);
  if (input.carry_forward) {
    form.append('carry_forward', 'true');
    if (input.carry_forward_from) {
      form.append('carry_forward_from', input.carry_forward_from);
    }
  }
  if (input.materiality_floor !== undefined) {
    form.append('materiality_floor', input.materiality_floor.toFixed(2));
  }
  return apiFetch<AuditRunOut>(
    `/api/engagements/${encodeURIComponent(engagementId)}/runs`,
    {
      idToken,
      method: 'POST',
      body: form,
    },
  );
}

/** Cumulative sweep-preview curve for the new-run form's materiality
 *  slider (INP-366). One response carries the whole $1..$100 curve so
 *  slider drags never refetch; empty when the engagement has no
 *  completed run to preview from. */
export function getMaterialityPreview(
  engagementId: string,
  idToken: string,
): Promise<MaterialityPreviewOut> {
  return apiClient
    .GET('/api/engagements/{engagement_id}/materiality-preview', {
      params: { path: { engagement_id: engagementId } },
      headers: bearer(idToken),
    })
    .then(unwrap<MaterialityPreviewOut>);
}

/** Reset every auto-carried-forward finding on a run back to pending.
 *  Powers the run-page filter chip's "drop carried dispositions" action.
 *  INP-385. */
export async function resetCarryForwardForRun(
  runId: string,
  idToken: string,
): Promise<{ reset_count: number }> {
  return apiFetch<{ reset_count: number }>(
    `/api/runs/${encodeURIComponent(runId)}/carry-forward:reset`,
    { idToken, method: 'POST' },
  );
}

/** Fetch the client-facing high-level PDF report for a completed run
 *  and trigger a browser download. Uses a fetch+Blob round-trip rather
 *  than a plain `<a href>` because the endpoint requires the Bearer
 *  token, which an anchor can't attach. The PDF is the one-pager the
 *  practitioner hands to the customer; it summarizes approved findings
 *  only (the server enforces the filter).
 *
 *  `annualRevenue` (CAD) is optional — when set, the PDF renders
 *  "Annual revenue: $X · Y% recovered" under the period subtitle.
 *  Plumbed as a query param until persisted per-engagement.
 *
 *  Blob/download endpoints stay on the raw `fetch` path — openapi-fetch
 *  is JSON-shaped and doesn't fit binary responses cleanly.
 */
export async function downloadRunReportPdf(
  runId: string,
  idToken: string,
  options?: { annualRevenue?: number },
): Promise<void> {
  const params = new URLSearchParams();
  if (options?.annualRevenue !== undefined && options.annualRevenue > 0) {
    params.set('annual_revenue', String(options.annualRevenue));
  }
  const query = params.toString();
  const url = `${API_BASE_URL}/api/runs/${encodeURIComponent(runId)}/report.pdf${query ? `?${query}` : ''}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || null);
  }
  const blob = await res.blob();
  const filename = parseContentDispositionFilename(res.headers.get('content-disposition'));
  triggerBlobDownload(blob, filename ?? `recovery-${runId.slice(0, 8)}.pdf`);
}

/** Download the client-facing recovery line-item CSV for a completed run
 *  — the spreadsheet counterpart to `downloadRunReportPdf`. One row per
 *  approved recovery finding; the server enforces the approved +
 *  recovery-outcome filter. Same blob-download rationale as the PDF. */
export async function downloadRunReportCsv(runId: string, idToken: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/runs/${encodeURIComponent(runId)}/report.csv`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || null);
  }
  const blob = await res.blob();
  const filename = parseContentDispositionFilename(res.headers.get('content-disposition'));
  triggerBlobDownload(blob, filename ?? `recovery-${runId.slice(0, 8)}.csv`);
}

// ── Filings ──────────────────────────────────────────────────────────

export function listFilings(runId: string, idToken: string): Promise<FilingOut[]> {
  return apiClient
    .GET('/api/runs/{run_id}/filings', {
      params: { path: { run_id: runId } },
      headers: bearer(idToken),
    })
    .then(unwrap<FilingOut[]>);
}

/** Commit the run's current approved-recovery delta as a new filing.
 *  422 when there's nothing newly approved to file — surface the
 *  preview's count to the user before calling. */
export function createFiling(
  runId: string,
  idToken: string,
  body: FilingCreate = {},
): Promise<FilingOut> {
  return apiClient
    .POST('/api/runs/{run_id}/filings', {
      params: { path: { run_id: runId } },
      headers: bearer(idToken),
      body,
    })
    .then(unwrap<FilingOut>);
}

/** Count + recovery total of what the *next* filing would contain —
 *  drives the Create-filing affordance (disabled at count 0). */
export function filingPreview(runId: string, idToken: string): Promise<FilingPreviewOut> {
  return apiClient
    .GET('/api/runs/{run_id}/filings/preview', {
      params: { path: { run_id: runId } },
      headers: bearer(idToken),
    })
    .then(unwrap<FilingPreviewOut>);
}

/** Un-file the run's latest filing (409 for any other), releasing its
 *  findings back into the next delta. */
export function deleteFiling(runId: string, filingId: string, idToken: string): Promise<void> {
  return apiClient
    .DELETE('/api/runs/{run_id}/filings/{filing_id}', {
      params: { path: { run_id: runId, filing_id: filingId } },
      headers: bearer(idToken),
    })
    .then(unwrap<void>);
}

/** Per-filing PDF report — the run report scoped to the filing's frozen
 *  members. Same blob-download rationale as `downloadRunReportPdf`. */
export async function downloadFilingReportPdf(
  runId: string,
  filingId: string,
  idToken: string,
): Promise<void> {
  const url = `${API_BASE_URL}/api/runs/${encodeURIComponent(runId)}/filings/${encodeURIComponent(filingId)}/report.pdf`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || null);
  }
  const blob = await res.blob();
  const filename = parseContentDispositionFilename(res.headers.get('content-disposition'));
  triggerBlobDownload(blob, filename ?? `recovery-${runId.slice(0, 8)}-filing.pdf`);
}

/** Per-filing recovery CSV — the run `report.csv` scoped to the filing's
 *  frozen members. */
export async function downloadFilingReportCsv(
  runId: string,
  filingId: string,
  idToken: string,
): Promise<void> {
  const url = `${API_BASE_URL}/api/runs/${encodeURIComponent(runId)}/filings/${encodeURIComponent(filingId)}/report.csv`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || null);
  }
  const blob = await res.blob();
  const filename = parseContentDispositionFilename(res.headers.get('content-disposition'));
  triggerBlobDownload(blob, filename ?? `recovery-${runId.slice(0, 8)}-filing.csv`);
}

// ── Orphan ITC entries ───────────────────────────────────────────────

/** The run's orphan ITC entries — journal-entry lines that post a
 *  GST/HST amount to a tax account with no expense base on the same
 *  transaction (a missed HST booked as its own stray entry). Read-only
 *  surfacing; no matching to the taxable transaction yet. */
export function listOrphanItcEntries(
  runId: string,
  idToken: string,
): Promise<OrphanItcEntriesOut> {
  return apiClient
    .GET('/api/runs/{run_id}/orphan-itc-entries', {
      params: { path: { run_id: runId } },
      headers: bearer(idToken),
    })
    .then(unwrap<OrphanItcEntriesOut>);
}

/** Download every finding for a run as CSV. Same blob-download
 *  rationale as `downloadRunReportPdf`. */
export async function downloadRunFindingsCsv(runId: string, idToken: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/runs/${encodeURIComponent(runId)}/findings.csv`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || null);
  }
  const blob = await res.blob();
  const filename = parseContentDispositionFilename(res.headers.get('content-disposition'));
  triggerBlobDownload(blob, filename ?? `findings-${runId.slice(0, 8)}.csv`);
}

/** Fetch a presigned URL for the run's normalized CSV and trigger the
 *  browser download. Bytes flow Browser → S3 directly. */
export async function downloadRunNormalizedCsv(
  runId: string,
  idToken: string,
): Promise<void> {
  const out = await apiClient
    .GET('/api/runs/{run_id}/normalized-download', {
      params: { path: { run_id: runId } },
      headers: bearer(idToken),
    })
    .then(
      unwrap<{
        url: string;
        filename: string;
        byte_count: number;
        expires_in: number;
      }>,
    );

  // The presigned URL is good for ~1 hour; no need to bounce through fetch
  // again — point an invisible anchor at it and click. S3 supplies the bytes
  // and the `download` attr suggests the filename.
  const a = document.createElement('a');
  a.href = out.url;
  a.download = out.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="([^"]+)"/.exec(header);
  return match ? match[1] : null;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
